import { ReactNode } from 'react';
import { Icon, piIconName } from './icon';

export interface ScreenHeaderProps {
  /** PrimeIcon class, e.g. 'pi pi-shield', or a registry name like 'handshake'. */
  icon: string;
  /** Tailwind background for the icon tile — the screen's identity colour. */
  iconBgColor?: string;
  title: string;
  subtitle?: string;
  /** Right-aligned content: metric chips, primary action, etc. */
  right?: ReactNode;
  /** When set, a back button renders before the icon tile (used by form/view sub-pages). */
  onBack?: () => void;
  testId?: string;
}

/**
 * The one screen header for the whole admin area: a solid colour icon tile, the
 * title beside it, and a muted subtitle indented under both.
 *
 * It exists because the same header was written twice with different
 * proportions — the tabbed group screens used a compact solid tile, while
 * directly-routed list pages used a larger soft-tinted one. Navigating between a
 * grouped screen and a standalone one visibly changed the page furniture. This is
 * the compact form (the one Access Control has always used); PageHeader and
 * GroupScreen both render it now, so the two cannot drift apart again.
 */
const ScreenHeader = ({
  icon,
  // Default to the signature brand gradient tile; screens can still pass a solid tint.
  iconBgColor = 'bg-brand-gradient-br',
  title,
  subtitle,
  right,
  onBack,
  testId,
}: ScreenHeaderProps) => (
  <div className="mb-4 flex flex-wrap items-start justify-between gap-3" data-testid={testId}>
    <div>
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-canvas transition-colors"
          >
            <Icon name="arrow-left" size={18} />
          </button>
        )}
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm ${iconBgColor}`}>
          {/* Same rule the sidebar and tab strip already follow: render Lucide
              where the name maps, fall back to the PrimeIcon where it does not. */}
          {piIconName(icon)
            ? <Icon name={piIconName(icon)!} size={20} className="text-white" />
            : <i className={`${icon} text-base text-white`} />}
        </div>
        <h1 className="text-title font-semibold tracking-tight text-ink">{title}</h1>
      </div>
      {subtitle && (
        <p className={`${onBack ? 'ml-[100px]' : 'ml-[52px]'} text-body-sm text-ink-muted`}>{subtitle}</p>
      )}
    </div>

    {right && <div className="flex flex-wrap items-center gap-2.5">{right}</div>}
  </div>
);

export default ScreenHeader;
